/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../core'

import {KTCard, KTCardBody, Notes, Category, DataTables, Loading} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {purchaseProposalCompanyGetId} from '../../../redux/slices/purchase.slice'
import {SourceType, KTSVG} from '../../../helpers'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'

import {
  useMemoizedPageUrl,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  detail: state.purchase.detail,
  loading: state.purchase.loading
})

function Detail() {
  const {id} = useParams()
  const location = useLocation()
  const intl = useIntl()
  const dispatch = useDispatch()

  const {detail, loading} = useSelector(selectPurchaseData)

  const pageUrl = useMemoizedPageUrl(location)

  useEffect(() => {
    dispatch(purchaseProposalCompanyGetId(id))
  }, [id, dispatch])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.PROPOSALCOMPANY'}),
      path: '/purchase/proposal-company',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  const dataTableColumns = useMemo(() => [
    {
      title: intl.formatMessage({
        id: 'COMPANY.CREATE.COMPANYPEOPLE.NAMESURNAME',
      }),
      prop: 'nameSurname',
      isSortable: true,
      isFilterable: true,
    },
    {
      title: intl.formatMessage({id: 'COMPANY.CREATE.COMPANYPEOPLE.PHONE'}),
      prop: 'phone',
      isSortable: true,
      isFilterable: true,
    },
    {
      title: intl.formatMessage({id: 'COMPANY.CREATE.COMPANYPEOPLE.EMAIL'}),
      prop: 'email',
      isSortable: true,
      isFilterable: true,
    },
    {
      title: intl.formatMessage({
        id: 'COMPANY.CREATE.COMPANYPEOPLE.DESCRIPTION',
      }),
      prop: 'description',
      isSortable: true,
      isFilterable: true,
    },
  ], [intl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.PROPOSALCOMPANY.DETAIL'})}
      </PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>{detail.name}</div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            {detail && detail.fullName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/duotune/finance/fin006.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.fullName}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Category
                            SourceId={Number(id)}
                            SourceTypeId={SourceType.ProposalCompany}
                          />

                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.EMAIL'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail && detail.email}</div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'PURCHASEPROPOSALCOMPANY.CREATE.PHONE'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail && detail.phone}</div>
                          </div>
                          {detail && detail.address && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({
                                    id: 'PURCHASEPROPOSALCOMPANY.CREATE.ADDRESS',
                                  })}
                                </div>
                                <div className='fw-bold fs-4'>{detail && detail.address}</div>
                              </div>
                            </>
                          )}
                          {detail && detail.responsibleInfo && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({
                                    id: 'PURCHASEPROPOSALCOMPANY.CREATE.RESPONSIBLEINFO',
                                  })}
                                </div>
                                <div className='fw-bold fs-4'>{detail && detail.responsibleInfo}</div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {detail && detail.note && (
                    <>
                      <div className='notice d-flex rounded border-primary border border-dashed  p-6'>
                        {detail && detail.note}
                      </div>
                    </>
                  )}
                </KTCardBody>
              </KTCard>

              <KTCard>
                <KTCardBody className='p-9'>
                  <Tabs className='nav-line-tabs' fill>
                    {detail && detail.proposalCompanyPeoples && detail.proposalCompanyPeoples.length > 0 && (
                      <Tab
                        eventKey='proposalCompanyPeoples'
                        title={intl.formatMessage({id: 'COMPANY.GET.COMPANYPEOPLE'})}
                      >
                        <DataTables
                          columns={dataTableColumns}
                          data={detail && detail.proposalCompanyPeoples}
                          exportTitle={intl.formatMessage({id: 'COMPANY.GET.COMPANYPEOPLE'})}
                        />
                      </Tab>
                    )}
                  </Tabs>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>

        <div className='col-xl-3'>
          <Notes SourceId={Number(id)} SourceTypeId={SourceType.ProposalCompany} />
        </div>
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Detail}
